import {Component, ViewEncapsulation, computed, inject, input} from '@angular/core';
import {DomSanitizer} from '@angular/platform-browser';
import {Router} from '@angular/router';
import {Marked} from 'marked';
import DOMPurify from 'dompurify';
import {TapHelperService} from './tap-helper.service';

const md = new Marked({gfm: true, breaks: true});

// Own instance so the hook doesn't leak into any other DOMPurify user.
const purify = DOMPurify(window);
purify.addHook('afterSanitizeAttributes', node => {
  if (node instanceof HTMLAnchorElement && /^https?:/i.test(node.getAttribute('href') ?? '')) {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
  }
});

function render(text: string): string {
  return purify.sanitize(md.parse(text, {async: false}) as string, {
    USE_PROFILES: {html: true},
    FORBID_TAGS: ['style', 'img', 'form', 'input'],
    FORBID_ATTR: ['style'],
  });
}

/** Assistant reply as sanitised markdown. In-app links route (and minimise the chat) instead of reloading the page. */
@Component({
  selector: 'chat-markdown',
  template: '',
  styleUrl: './chat-markdown.scss',
  encapsulation: ViewEncapsulation.None,
  host: {
    class: 'chat-md block min-w-0',
    '[innerHTML]': 'html()',
    '(click)': 'onClick($event)',
  },
})
export class ChatMarkdown {
  private sanitizer = inject(DomSanitizer);
  private router = inject(Router);
  private chat = inject(TapHelperService);

  text = input.required<string>();
  // Safe to bypass: the string comes straight from DOMPurify.
  protected html = computed(() => this.sanitizer.bypassSecurityTrustHtml(render(this.text())));

  onClick(event: MouseEvent): void {
    const link = (event.target as Element | null)?.closest('a');
    const href = link?.getAttribute('href');
    if (!link || !href?.startsWith('/') || href.startsWith('//') || link.target
      || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.defaultPrevented) {
      return;
    }
    event.preventDefault();
    this.router.navigateByUrl(href);
    this.chat.minimise();
  }
}
